import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, useAnimate } from "framer-motion";
import { useServerStatus } from "./useServerStatus";
import { useEffect, useState } from "react";

type ServerStatusProps = {
  randomize?: number;
};

export function ServerStatus({ randomize }: ServerStatusProps) {
  const { servers } = useServerStatus(randomize);

  return (
    <div className="container mx-auto px-4 py-4 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {servers.map(({ name, url, status, color, responseTime, uptime, bgColorStyle, size, lastUpdate }) => {
          return <ServerCard
            key={name}
            name={name}
            url={url}
            status={status}
            color={color}
            responseTime={responseTime}
            uptime={uptime}
            bgColorStyle={bgColorStyle}
            size={size}
            lastUpdate={lastUpdate}
          />;
        })}
      </div>
    </div>
  );
}

function ServerCard({ name, url, status, color, responseTime, uptime, bgColorStyle, size, lastUpdate }: {
  name: string;
  url: string;
  status: string;
  color: string;
  responseTime: string;
  uptime: string;
  bgColorStyle: string;
  size?: string;
  lastUpdate?: number;
}) {
  const [scope, animate] = useAnimate();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (lastUpdate) {
      const glowColor = color === 'bg-green-500'
        ? 'rgba(34, 197, 94, 0.6)'
        : color === 'bg-yellow-500'
          ? 'rgba(234, 179, 8, 0.6)'
          : 'rgba(239, 68, 68, 0.6)';

      const brightColor = color === 'bg-green-500'
        ? 'rgba(34, 197, 94, 0.3)'
        : color === 'bg-yellow-500'
          ? 'rgba(234, 179, 8, 0.3)'
          : 'rgba(239, 68, 68, 0.3)';

      animate(scope.current,
        {
          scale: [1, 1.02, 1],
          boxShadow: [
            '0 0 0px rgba(34, 197, 94, 0)',
            `0 0 20px ${glowColor}`,
            '0 0 0px rgba(34, 197, 94, 0)'
          ],
          backgroundColor: [bgColorStyle, brightColor, bgColorStyle]
        },
        { duration: 0.5 }
      );
    }
  }, [lastUpdate, animate, scope, color, bgColorStyle]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card ref={scope} style={{ backgroundColor: bgColorStyle }} className="py-2 md:py-6">
        <CardHeader 
          className="md:cursor-default cursor-pointer py-2 px-4 md:py-6" 
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base md:text-lg">{name}</CardTitle>
            <Badge variant="default" className={`${color} text-white font-medium text-xs md:text-base px-2 md:px-3 py-0.5 md:py-1`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className={`md:block ${isExpanded ? 'block' : 'hidden'}`}>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Response Time</span>
              <span className="font-medium">{responseTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Response Size</span>
              <span className="font-medium">{size || 'N/A'}</span>
            </div>
            <div className="text-muted-foreground truncate" title={url}>
              {url}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
